import { useState, useCallback, useRef, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { logger } from '../../utils/logger';
import { jetonTempsReel } from '../../utils/jetonTempsReel';

// Remove /api suffix if present for WebSocket connection
const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');

// Global registry to track active WebRTC connections per session
// This helps prevent cleanup of connections when React StrictMode remounts components
const activeConnections: Map<string, {
  instanceId: number;
  socket: Socket | null;
  pc: RTCPeerConnection | null;
  localStream: MediaStream | null;
}> = new Map();

// Global camera stream - persists across all component instances
// This ensures the camera is never accidentally stopped by React lifecycle
let globalCameraStream: MediaStream | null = null;

// Counter for unique instance IDs
let instanceCounter = 0;

export type VideoQuality = '480p' | '720p' | '1080p';

export interface WebRTCConfig {
  iceServers: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
}

export interface VideoConstraints {
  '480p': MediaTrackConstraints;
  '720p': MediaTrackConstraints;
  '1080p': MediaTrackConstraints;
}

interface UseWebRTCOptions {
  sessionId: string;
  tenantId: string;
  role: 'vendor' | 'customer';
  vendorId?: string;
  customerId?: string;
  onRemoteStream?: (stream: MediaStream) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onError?: (error: Error) => void;
}

interface UseWebRTCReturn {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionState: RTCPeerConnectionState | null;
  error: string | null;

  // Local stream (vendor: video+audio, customer: audio only)
  localStream: MediaStream | null;
  startCamera: (quality?: VideoQuality) => Promise<void>;
  stopCamera: () => void;

  // Customer microphone (audio only)
  startMicrophone: () => Promise<void>;
  stopMicrophone: () => void;
  isMicrophoneActive: boolean;

  // Remote stream
  remoteStream: MediaStream | null;

  // Connection control
  connect: () => Promise<void>;
  disconnect: () => void;

  // Video quality
  setVideoQuality: (quality: VideoQuality) => void;
  currentQuality: VideoQuality;

  // Stats
  stats: RTCStatsReport | null;
}

export function useWebRTC(options: UseWebRTCOptions): UseWebRTCReturn {
  const {
    sessionId,
    tenantId,
    role,
    vendorId,
    customerId,
    onRemoteStream,
    onConnectionStateChange,
    onError,
  } = options;

  // Initialize localStream from globalCameraStream if available (handles StrictMode remount)
  // This ensures the camera stream survives React lifecycle
  const getInitialLocalStream = () => {
    if (globalCameraStream) {
      const hasActiveTracks = globalCameraStream.getTracks().some(t => t.readyState === 'live');
      if (hasActiveTracks) {
        return globalCameraStream;
      } else {
        globalCameraStream = null;
      }
    }
    return null;
  };

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(getInitialLocalStream);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [currentQuality, setCurrentQuality] = useState<VideoQuality>('720p');
  const [stats, setStats] = useState<RTCStatsReport | null>(null);
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);
  const [microphoneStream, setMicrophoneStream] = useState<MediaStream | null>(null);

  // Refs
  const socketRef = useRef<Socket | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const configRef = useRef<WebRTCConfig | null>(null);
  const videoConstraintsRef = useRef<VideoConstraints | null>(null);
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null); // Track local stream for registry
  const isMountedRef = useRef(false);
  const connectingRef = useRef(false); // Prevent multiple concurrent connect calls
  const hasFailedRef = useRef(false); // Prevent reconnection loop after failure
  const instanceIdRef = useRef(0); // Will be set in mount effect

  // Callback refs to avoid stale closures
  const onRemoteStreamRef = useRef(onRemoteStream);
  const onConnectionStateChangeRef = useRef(onConnectionStateChange);
  const onErrorRef = useRef(onError);

  // Keep callback refs updated
  useEffect(() => {
    onRemoteStreamRef.current = onRemoteStream;
    onConnectionStateChangeRef.current = onConnectionStateChange;
    onErrorRef.current = onError;
  });

  // Keep localStreamRef in sync with state
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  // Reset refs on each mount to allow fresh connections
  useEffect(() => {
    // Generate new instance ID on each mount (important for StrictMode)
    const myInstanceId = ++instanceCounter;
    instanceIdRef.current = myInstanceId;

    // Reset flags to allow new connection on remount
    connectingRef.current = false;
    hasFailedRef.current = false;

    return () => {
      // Cleanup on unmount
    };
  }, [sessionId]);

  // Fetch WebRTC config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/live-shopping/webrtc-config`, {
          headers: {
            'x-tenant-id': tenantId,
          },
        });
        const data = await response.json();
        configRef.current = data.config;
        videoConstraintsRef.current = data.videoConstraints;
      } catch (err) {
        logger.error('Failed to fetch WebRTC config:', err);
        // Use default config
        configRef.current = {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
        };
      }
    };
    fetchConfig();
  }, [tenantId]);

  // Create peer connection
  const createPeerConnection = useCallback(() => {
    if (!configRef.current) {
      throw new Error('WebRTC config not loaded');
    }

    const pc = new RTCPeerConnection(configRef.current);

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice_candidate', {
          tenant_id: tenantId,
          session_id: sessionId,
          candidate: event.candidate,
        });
      }
    };

    // Track the last stream we notified about to avoid duplicate callbacks
    let lastNotifiedStreamId: string | null = null;

    pc.ontrack = (event) => {
      const stream = event.streams[0];
      if (!stream) return;

      setRemoteStream(stream);

      // Only call callback once per unique stream to avoid multiple play() calls
      if (stream.id !== lastNotifiedStreamId) {
        lastNotifiedStreamId = stream.id;
        // Use ref to get current callback (avoids stale closure)
        onRemoteStreamRef.current?.(stream);
      }

      // If we're receiving tracks, consider us connected for UI purposes
      // (tracks can arrive before connectionState changes to 'connected')
      if (stream.active) {
        setIsConnected(true);
        setIsConnecting(false);
      }
    };

    pc.onconnectionstatechange = () => {
      setConnectionState(pc.connectionState);
      // Use ref to get current callback
      onConnectionStateChangeRef.current?.(pc.connectionState);

      if (pc.connectionState === 'connected') {
        setIsConnected(true);
        setIsConnecting(false);
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        setIsConnected(false);
        setError('Connexion vidéo perdue');
      }
    };

    pc.oniceconnectionstatechange = () => {
      // ICE state changed
    };

    peerConnectionRef.current = pc;
    return pc;
  }, [sessionId, tenantId]);

  // Start camera (vendor only)
  const startCamera = useCallback(async (quality: VideoQuality = '720p') => {
    // First check the global camera stream (most reliable)
    if (globalCameraStream) {
      const hasActiveTracks = globalCameraStream.getTracks().some(t => t.readyState === 'live');
      if (hasActiveTracks) {
        setLocalStream(globalCameraStream);
        setCurrentQuality(quality);
        return;
      } else {
        globalCameraStream = null;
      }
    }

    // Fallback: check registry
    const existingConnection = activeConnections.get(sessionId);
    if (existingConnection?.localStream) {
      const existingTracks = existingConnection.localStream.getTracks();
      const hasActiveTracks = existingTracks.some(t => t.readyState === 'live');
      if (hasActiveTracks) {
        globalCameraStream = existingConnection.localStream; // Also store globally
        setLocalStream(existingConnection.localStream);
        setCurrentQuality(quality);
        return;
      }
    }

    // Default constraints optimized for Logitech BRIO 505
    const defaultConstraints: Record<VideoQuality, MediaTrackConstraints> = {
      '480p': { width: { ideal: 854 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
      '720p': { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
      '1080p': { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
    };

    try {
      // First, check if any video device is available
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');

      if (videoDevices.length === 0) {
        throw new Error('Aucune caméra détectée. Veuillez brancher la caméra.');
      }

      // Use configured constraints or fallback to defaults
      const videoConstraints = videoConstraintsRef.current?.[quality] || defaultConstraints[quality];

      const constraints: MediaStreamConstraints = {
        video: videoConstraints,
        audio: true,
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch {
        // If constraints fail, try with basic constraints
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
      }

      // Store in global variable first (most important for persistence)
      globalCameraStream = stream;

      setLocalStream(stream);
      setCurrentQuality(quality);

      // Update registry with new stream immediately
      const current = activeConnections.get(sessionId);
      activeConnections.set(sessionId, {
        instanceId: current?.instanceId ?? instanceIdRef.current,
        socket: current?.socket ?? socketRef.current,
        pc: current?.pc ?? peerConnectionRef.current,
        localStream: stream,
      });

      // Add tracks to peer connection if it exists
      if (peerConnectionRef.current) {
        stream.getTracks().forEach((track) => {
          peerConnectionRef.current!.addTrack(track, stream);
        });
      }

      // Listen for track ended events to detect capture failures
      stream.getTracks().forEach((track) => {
        track.onended = () => {
          // Clear global stream if any track dies
          if (globalCameraStream === stream) {
            globalCameraStream = null;
          }
        };
      });
    } catch (err) {
      const error = err as Error;

      // Provide user-friendly error messages
      let message = error.message;
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        message = 'Aucune caméra détectée. Veuillez brancher la Logitech BRIO 505.';
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        message = 'Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres du navigateur.';
      } else if (error.name === 'OverconstrainedError') {
        message = 'La caméra ne supporte pas les paramètres demandés. Réessayez avec une qualité inférieure.';
      }

      setError(message);
      onErrorRef.current?.(new Error(message));
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }
    // Also clear global camera stream
    if (globalCameraStream) {
      globalCameraStream.getTracks().forEach((track) => track.stop());
      globalCameraStream = null;
    }
  }, [localStream]);

  // Start microphone (customer only - audio without video)
  const startMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      setMicrophoneStream(stream);
      setIsMicrophoneActive(true);

      // Add audio track to peer connection if it exists
      if (peerConnectionRef.current) {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          peerConnectionRef.current.addTrack(audioTrack, stream);
        }
      }
    } catch (err) {
      const error = err as Error;

      let message = error.message;
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        message = 'Aucun microphone détecté.';
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        message = 'Accès au microphone refusé. Veuillez autoriser l\'accès.';
      }

      setError(message);
      onErrorRef.current?.(new Error(message));
    }
  }, []);

  // Stop microphone
  const stopMicrophone = useCallback(() => {
    if (microphoneStream) {
      microphoneStream.getTracks().forEach((track) => track.stop());
      setMicrophoneStream(null);
      setIsMicrophoneActive(false);
    }
  }, [microphoneStream]);

  // Set video quality
  const setVideoQuality = useCallback((quality: VideoQuality) => {
    if (socketRef.current) {
      socketRef.current.emit('set_video_quality', {
        session_id: sessionId,
        quality,
      });
    }
    setCurrentQuality(quality);
  }, [sessionId]);

  // Connect to session
  const connect = useCallback(async () => {
    // Check if there's an existing active connection for this session we can reuse
    const existingConnection = activeConnections.get(sessionId);
    if (existingConnection && existingConnection.socket?.connected && existingConnection.pc?.connectionState !== 'closed') {
      // Take ownership of the existing connection
      socketRef.current = existingConnection.socket;
      peerConnectionRef.current = existingConnection.pc;

      // Reuse local stream if available AND has active tracks (vendor camera)
      if (existingConnection.localStream) {
        const hasActiveTracks = existingConnection.localStream.getTracks().some(t => t.readyState === 'live');
        if (hasActiveTracks) {
          setLocalStream(existingConnection.localStream);
        } else {
          existingConnection.localStream = null;
        }
      }

      // Update registry with our instance ID
      activeConnections.set(sessionId, {
        instanceId: instanceIdRef.current,
        socket: existingConnection.socket,
        pc: existingConnection.pc,
        localStream: existingConnection.localStream,
      });

      // Get remote stream from existing PC if available
      const pc = existingConnection.pc;
      if (pc) {
        const receivers = pc.getReceivers();
        const videoReceiver = receivers.find(r => r.track?.kind === 'video');
        if (videoReceiver?.track) {
          const stream = new MediaStream([videoReceiver.track]);
          // Also add audio track if available
          const audioReceiver = receivers.find(r => r.track?.kind === 'audio');
          if (audioReceiver?.track) {
            stream.addTrack(audioReceiver.track);
          }
          setRemoteStream(stream);
          onRemoteStreamRef.current?.(stream);
        }
      }

      setIsConnected(true);
      setIsConnecting(false);
      return;
    }

    // Prevent multiple concurrent connects and reconnection loops
    if (connectingRef.current || isConnected || socketRef.current?.connected) {
      return;
    }

    // Don't retry if we already failed
    if (hasFailedRef.current) {
      return;
    }

    connectingRef.current = true;
    setIsConnecting(true);
    setError(null);

    try {
      // Create socket connection - disable auto-reconnection to prevent loops
      const socket = io(`${API_URL}/live`, {
        transports: ['websocket', 'polling'],
        reconnection: false, // Disable auto-reconnection to prevent infinite loops
        auth: { token: jetonTempsReel() },
      });

      socketRef.current = socket;

      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Connection timeout')), 10000);

        socket.on('connect', () => {
          clearTimeout(timeout);
          resolve();
        });

        socket.on('connect_error', (err) => {
          clearTimeout(timeout);
          reject(err);
        });
      });

      // Join session
      const joinResult = await new Promise<{ success: boolean; error?: string }>((resolve) => {
        socket.emit(
          'join_session',
          {
            tenant_id: tenantId,
            session_id: sessionId,
            role,
            vendor_id: vendorId,
            customer_id: customerId,
          },
          resolve
        );
      });

      if (!joinResult.success) {
        throw new Error(joinResult.error || 'Failed to join session');
      }

      // Create peer connection
      const pc = createPeerConnection();

      // Update global registry with actual socket and pc references
      // Use localStreamRef.current to get the most up-to-date stream
      const currentLocalStream = localStreamRef.current || localStream;
      activeConnections.set(sessionId, {
        instanceId: instanceIdRef.current,
        socket,
        pc,
        localStream: currentLocalStream,
      });

      // If vendor, add local stream and create offer
      // Use localStreamRef.current to avoid stale closure issue
      if (role === 'vendor' && currentLocalStream) {
        logger.log('[useWebRTC] Vendor adding local stream tracks and creating offer...');
        currentLocalStream.getTracks().forEach((track) => {
          pc.addTrack(track, currentLocalStream);
        });

        // Create offer that accepts audio from customer (bidirectional audio)
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: false, // Customer doesn't send video
        });
        await pc.setLocalDescription(offer);

        socket.emit('webrtc_offer', {
          tenant_id: tenantId,
          session_id: sessionId,
          sdp: offer,
        });
        logger.log('[useWebRTC] ✅ Vendor sent initial WebRTC offer');
      } else if (role === 'vendor' && !currentLocalStream) {
        logger.warn('[useWebRTC] ⚠️ Vendor connected but no local stream available yet');
      }

      // Track if we're currently processing an offer to prevent race conditions
      let isProcessingOffer = false;
      // Track if we're currently sending an offer to prevent duplicate offers
      let isSendingOffer = false;
      // Queue ICE candidates until remote description is set
      let pendingIceCandidates: RTCIceCandidateInit[] = [];
      let remoteDescriptionSet = false;

      // Listen for WebRTC signaling
      socket.on('webrtc_offer', async (data: { sdp: RTCSessionDescriptionInit }) => {
        // Skip if we're already processing an offer
        if (isProcessingOffer) {
          return;
        }

        isProcessingOffer = true;

        try {
          // Check if PC is in a bad state and needs to be recreated
          if (pc.signalingState === 'closed' || pc.connectionState === 'failed') {
            // For now, just skip - user needs to refresh
            isProcessingOffer = false;
            return;
          }

          // If signaling state is not stable, we need to rollback first
          // But only if we're in have-local-offer state (rollback only works in certain states)
          if (pc.signalingState === 'have-local-offer') {
            try {
              await pc.setLocalDescription({ type: 'rollback' });
            } catch {
              // Rollback failed, continuing anyway
            }
          } else if (pc.signalingState !== 'stable') {
            isProcessingOffer = false;
            return;
          }

          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          remoteDescriptionSet = true;

          // Process any queued ICE candidates
          if (pendingIceCandidates.length > 0) {
            for (const candidate of pendingIceCandidates) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch {
                // Error adding queued ICE candidate
              }
            }
            pendingIceCandidates = [];
          }

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          socket.emit('webrtc_answer', {
            tenant_id: tenantId,
            session_id: sessionId,
            sdp: answer,
          });
        } catch {
          // Error handling offer
        } finally {
          isProcessingOffer = false;
        }
      });

      socket.on('webrtc_answer', async (data: { sdp: RTCSessionDescriptionInit }) => {
        // Only process answer if we're in the correct state (waiting for an answer)
        if (pc.signalingState !== 'have-local-offer') {
          return;
        }

        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          remoteDescriptionSet = true;

          // Process any queued ICE candidates
          if (pendingIceCandidates.length > 0) {
            for (const candidate of pendingIceCandidates) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch {
                // Error adding queued ICE candidate
              }
            }
            pendingIceCandidates = [];
          }
        } catch {
          // Error handling answer
        }
      });

      socket.on('ice_candidate', async (data: { candidate: RTCIceCandidateInit }) => {
        // Queue candidates if remote description not yet set
        if (!remoteDescriptionSet) {
          pendingIceCandidates.push(data.candidate);
          return;
        }

        try {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch {
          // Error adding ICE candidate
        }
      });

      socket.on('video_quality_requested', () => {
        // TODO: Adjust video quality dynamically
      });

      socket.on('participant_left', (data: { socket_id?: string; role?: string }) => {
        // Ne pas déconnecter si c'est nous-mêmes qui partons (cleanup StrictMode)
        // ou si c'est un autre client du même rôle
        if (data.socket_id === socket.id) {
          return;
        }
        // Seulement mettre à jour l'UI si c'est le partenaire de l'autre rôle
        // Ne PAS fermer le PC - on va le réutiliser quand le partenaire revient
        const isPartnerLeaving = (role === 'customer' && data.role === 'vendor') ||
                                  (role === 'vendor' && data.role === 'customer');
        if (isPartnerLeaving) {
          setIsConnected(false);
          // Reset flag to allow processing new offers when partner reconnects
          isProcessingOffer = false;
        }
      });

      // Handle request to resend WebRTC offer (when customer reconnects)
      socket.on('request_webrtc_offer', async () => {
        // Use localStreamRef.current to get the most up-to-date stream
        const currentLocalStream = localStreamRef.current;

        logger.log('[useWebRTC] Received request_webrtc_offer', {
          role,
          hasLocalStream: !!currentLocalStream,
          signalingState: pc.signalingState,
          isSendingOffer,
        });

        if (role === 'vendor' && currentLocalStream && pc.signalingState !== 'closed') {
          // Prevent duplicate offers
          if (isSendingOffer) {
            logger.log('[useWebRTC] Already sending offer, skipping');
            return;
          }
          isSendingOffer = true;

          try {
            logger.log('[useWebRTC] Vendor creating and sending WebRTC offer...');
            // Create and send new offer (accept audio from customer)
            const offer = await pc.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: false,
            });
            await pc.setLocalDescription(offer);

            socket.emit('webrtc_offer', {
              tenant_id: tenantId,
              session_id: sessionId,
              sdp: offer,
            });
            logger.log('[useWebRTC] ✅ Vendor sent WebRTC offer');
          } catch (err) {
            logger.error('[useWebRTC] Error resending offer:', err);
          } finally {
            // Reset flag after a delay to allow for answer processing
            setTimeout(() => { isSendingOffer = false; }, 2000);
          }
        } else {
          logger.warn('[useWebRTC] Cannot send offer:', {
            reason: role !== 'vendor' ? 'not vendor' : !currentLocalStream ? 'no localStream' : 'PC closed',
          });
        }
      });

      // Handle participant joined - vendor should send offer to new customer
      // Customer should also reset state when vendor joins
      socket.on('participant_joined', async (data: { role: string; socket_id: string }) => {
        // Use localStreamRef.current to get the most up-to-date stream
        const currentLocalStream = localStreamRef.current;

        // Vendor: send offer to new customer
        if (role === 'vendor' && data.role === 'customer' && currentLocalStream && pc.signalingState !== 'closed') {
          // Prevent duplicate offers
          if (isSendingOffer) {
            return;
          }
          isSendingOffer = true;

          try {
            // Create offer that accepts audio from customer
            const offer = await pc.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: false,
            });
            await pc.setLocalDescription(offer);

            socket.emit('webrtc_offer', {
              tenant_id: tenantId,
              session_id: sessionId,
              sdp: offer,
            });
          } catch {
            // Error sending offer to new customer
          } finally {
            // Reset flag after a delay to allow for answer processing
            setTimeout(() => { isSendingOffer = false; }, 2000);
          }
        }

        // Customer: when vendor rejoins, reset flag to accept new offers
        if (role === 'customer' && data.role === 'vendor') {
          isProcessingOffer = false;
          // Mark as not connected until we receive the new offer
          setIsConnected(false);
        }
      });

      // Handle request from server to send WebRTC offer (when vendor rejoins session)
      socket.on('send_webrtc_offer_to_session', async () => {
        // Use localStreamRef.current to get the most up-to-date stream
        const currentLocalStream = localStreamRef.current;

        if (role === 'vendor' && currentLocalStream && pc.signalingState !== 'closed') {
          // Prevent duplicate offers
          if (isSendingOffer) {
            return;
          }
          isSendingOffer = true;

          try {
            // Create and send new offer
            const offer = await pc.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: false,
            });
            await pc.setLocalDescription(offer);

            socket.emit('webrtc_offer', {
              tenant_id: tenantId,
              session_id: sessionId,
              sdp: offer,
            });
          } catch {
            // Error sending offer after rejoin
          } finally {
            // Reset flag after a delay to allow for answer processing
            setTimeout(() => { isSendingOffer = false; }, 2000);
          }
        }
      });

      // NOTE: Customer microphone is NOT captured automatically
      // This avoids Safari issue where getUserMedia() in one tab can interrupt
      // media streams in other tabs (vendor's camera gets killed)
      // Customer can manually activate microphone via startMicrophone() if needed

      // If customer, request offer from vendor after a delay if not received
      // This handles the case where customer joins before vendor is ready
      if (role === 'customer') {
        const offerRequestTimeout = setTimeout(() => {
          // If we still don't have a remote description after 3 seconds, request offer
          if (!remoteDescriptionSet && socket.connected) {
            logger.log('[useWebRTC] Customer requesting WebRTC offer from vendor (retry)');
            socket.emit('request_webrtc_offer', {
              tenant_id: tenantId,
              session_id: sessionId,
            });
          }
        }, 3000);

        // Also retry every 5 seconds until we get the offer (max 3 retries)
        let retryCount = 0;
        const maxRetries = 3;
        const retryInterval = setInterval(() => {
          if (!remoteDescriptionSet && socket.connected && retryCount < maxRetries) {
            retryCount++;
            logger.log(`[useWebRTC] Customer retry ${retryCount}/${maxRetries} - requesting offer`);
            socket.emit('request_webrtc_offer', {
              tenant_id: tenantId,
              session_id: sessionId,
            });
          } else if (remoteDescriptionSet || retryCount >= maxRetries) {
            clearInterval(retryInterval);
          }
        }, 5000);

        // Cleanup timeouts when offer is received
        socket.once('webrtc_offer', () => {
          clearTimeout(offerRequestTimeout);
          clearInterval(retryInterval);
        });
      }

      // Start stats collection
      statsIntervalRef.current = setInterval(async () => {
        if (pc.connectionState === 'connected') {
          const stats = await pc.getStats();
          setStats(stats);
        }
      }, 5000);

    } catch (err) {
      const error = err as Error;
      setError(error.message);
      setIsConnecting(false);
      connectingRef.current = false;
      hasFailedRef.current = true; // Mark as failed to prevent reconnection loops

      // Cleanup socket if it was created
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      onErrorRef.current?.(error);
    }
  }, [
    isConnected,
    tenantId,
    sessionId,
    role,
    vendorId,
    customerId,
    localStream,
    createPeerConnection,
  ]);

  // Disconnect
  const disconnect = useCallback(() => {
    // Stop stats collection
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Disconnect socket
    if (socketRef.current) {
      socketRef.current.emit('leave_session', { session_id: sessionId });
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Stop local stream (vendor camera)
    stopCamera();

    // Stop microphone stream (customer)
    if (microphoneStream) {
      microphoneStream.getTracks().forEach((track) => track.stop());
      setMicrophoneStream(null);
      setIsMicrophoneActive(false);
    }

    // Reset state
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionState(null);
    setRemoteStream(null);
    setError(null);

    // Reset refs to allow new connection
    connectingRef.current = false;
    hasFailedRef.current = false;
  }, [sessionId, stopCamera, microphoneStream]);

  // Track mount state - NO automatic cleanup to avoid StrictMode issues
  // Cleanup is only done via explicit disconnect() call
  useEffect(() => {
    const myInstanceId = instanceIdRef.current;
    const mySessionId = sessionId;
    isMountedRef.current = true;

    // Register this instance in the global registry
    activeConnections.set(mySessionId, {
      instanceId: myInstanceId,
      socket: socketRef.current,
      pc: peerConnectionRef.current,
      localStream: localStreamRef.current,
    });

    return () => {
      isMountedRef.current = false;
      // NO automatic cleanup - connections persist until explicit disconnect()
      // This prevents StrictMode from killing active connections
    };
  }, [sessionId]);

  return {
    isConnected,
    isConnecting,
    connectionState,
    error,
    localStream,
    startCamera,
    stopCamera,
    startMicrophone,
    stopMicrophone,
    isMicrophoneActive,
    remoteStream,
    connect,
    disconnect,
    setVideoQuality,
    currentQuality,
    stats,
  };
}
